// Darstellung des Bausteins „Gebetszeiten": Tabelle quer, Spalte hochkant oder kompakte Leiste.
// Aktuelles Gebet hervorgehoben, Countdown zur nächsten Zeit, Jumu'ah freitags, Hijri- und gregorianisches Datum,
// Hinweis während der Iqama. Arabisch von rechts nach links. Text nur über textContent (DQ-SEC-030).
import { safeColor, safeTimeZone } from './analog.js';
import { clear, cssSafe, h, placeholder } from './dom.js';
import { PRAYER_KEYS, PRAYER_TEXTS, formatCountdown, gregorianText, hhmmAt, hijriText, isPrayerLang, prayerState, tableRef, } from './prayer.js';
// Grössen: `size` (cqh der Folie) ist die Obergrenze der Grundschrift. .wl-prayer ist Container (container-type: size),
// .wp-in skaliert die Grundschrift auf die Box des Bausteins herunter (cqw/cqh beziehen sich dort auf die Box).
// Zellen der Tabelle sind selbst Container: Zeiten, Namen und Iqama richten sich nach deren Breite und Höhe. In der
// Spalte gilt die Breite des Bausteins; die Zeilen teilen sich die Spalten per subgrid, damit die Zeiten untereinander
// stehen. --n ist die Zeichenzahl des Textes. Danach misst prayerFitter() im Browser nach und verkleinert bei Bedarf
// alles gemeinsam (--wp-fit). Auslassungspunkte bei Namen nur, wenn auch das nicht reicht.
export const PRAYER_CSS = `
    .wl-prayer { position: relative; width: 100%; height: 100%; box-sizing: border-box; container-type: size; line-height: 1.15; }
    .wl-prayer .wp-in { position: relative; width: 100%; height: 100%; box-sizing: border-box; display: flex; flex-direction: column; gap: .45em;
      padding: .55em .8em; overflow: hidden; font-size: calc(min(1em, var(--wp-cap)) * var(--wp-fit, 1)); }
    .wl-prayer.wp-tbl { --wp-cap: min(10cqh, 4.4cqw); }
    .wl-prayer.wp-col { --wp-cap: min(5cqh, 6cqw); }
    .wl-prayer.wp-bar { --wp-cap: min(52cqh, calc(100cqw / var(--wp-len, 50))); }
    .wl-prayer .wp-ph { display: flex; justify-content: space-between; align-items: flex-end; gap: .3em 1.2em; flex-wrap: wrap; flex: none; }
    .wl-prayer .wp-ttl { font-weight: 800; font-size: min(1.15em, calc(92cqw / (var(--n) * .62))); max-width: 100%;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .wl-prayer .wp-dates { display: flex; flex-direction: column; gap: .1em; font-size: .78em; opacity: .85; min-width: 0; }
    .wl-prayer .wp-dates span { font-size: min(1em, calc(92cqw / (var(--n) * .5))); white-space: nowrap; }
    .wl-prayer .wp-cd { display: flex; gap: .3em; align-items: baseline; font-weight: 700; white-space: nowrap; min-width: 0; max-width: 100%;
      font-size: min(1em, calc(92cqw / (var(--n, 20) * .56))); }
    .wl-prayer .wp-cd span { min-width: 0; overflow: hidden; text-overflow: ellipsis; }
    .wl-prayer .wp-cd b { flex: none; }
    .wl-prayer .wp-cd b, .wl-prayer .wp-tm { direction: ltr; unicode-bidi: isolate; font-variant-numeric: tabular-nums; white-space: nowrap; }
    .wl-prayer .wp-grid { flex: 1 1 0; min-height: 0; display: grid; grid-auto-flow: column; grid-auto-columns: minmax(0, 1fr); grid-template-rows: minmax(0, 1fr);
      gap: .45em; }
    .wl-prayer .wp-cell { container-type: size; display: grid; grid-template-rows: minmax(0, 1fr) auto minmax(0, 1fr); justify-items: center; row-gap: .12em;
      min-width: 0; min-height: 0; overflow: hidden; border-radius: .35em; background: rgba(255,255,255,.07); padding: .3em .15em; text-align: center; }
    .wl-prayer .wp-cell .wp-n { align-self: end; font-size: min(.8em, 14cqh, calc(96cqw / (var(--n) * .6))); opacity: .9; max-width: 100%;
      overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; overflow-wrap: normal; }
    .wl-prayer .wp-cell .wp-tm { font-size: min(1.75em, 34cqw, 34cqh); font-weight: 800; }
    .wl-prayer .wp-cell .wp-iq { align-self: start; font-size: min(.7em, 13cqh, calc(96cqw / (var(--n) * .58))); opacity: .85; max-width: 100%; }
    .wl-prayer .wp-rows { flex: 1 1 0; min-height: 0; display: grid; grid-template-columns: minmax(0, 1fr) auto auto; align-content: space-evenly; gap: .25em 0; }
    /* Zeilen ohne eigenen Container (Containment verhindert subgrid); cqw beziehen sich auf die Box des Bausteins */
    .wl-prayer .wp-row { grid-column: 1 / -1; display: grid; grid-template-columns: minmax(0, 1fr) auto auto;
      grid-template-columns: subgrid; align-items: center; gap: .3em .8em; padding: .3em .55em; border-radius: .3em; background: rgba(255,255,255,.06); }
    .wl-prayer .wp-row .wp-n { font-weight: 600; font-size: min(1em, calc(40cqw / (var(--n) * .62))); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .wl-prayer .wp-row .wp-tm { font-size: min(1.35em, 7.8cqw); font-weight: 800; }
    .wl-prayer .wp-row .wp-iq { font-size: min(.8em, calc(24cqw / (var(--n) * .58))); opacity: .85; white-space: nowrap; text-align: end; }
    .wl-prayer .wp-cur { background: var(--acc); color: var(--ink); }
    .wl-prayer .wp-cur .wp-n, .wl-prayer .wp-cur .wp-iq { opacity: 1; }
    .wl-prayer.wp-bar .wp-in { flex-direction: row; align-items: center; justify-content: space-between; gap: .7em; padding: .2em .6em; white-space: nowrap; }
    .wl-prayer.wp-bar .wp-it { display: flex; gap: .35em; align-items: baseline; padding: .15em .4em; border-radius: .3em; flex: 0 1 auto; min-width: 0; }
    .wl-prayer.wp-bar .wp-it .wp-n { min-width: 0; overflow: hidden; text-overflow: ellipsis; }
    .wl-prayer.wp-bar .wp-it .wp-tm { font-weight: 800; flex: none; }
    .wl-prayer.wp-bar .wp-cd { flex: 0 1 auto; font-size: 1em; }
    .wl-prayer .wp-notice { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; text-align: center; overflow: hidden;
      background: var(--acc); color: var(--ink); font-weight: 800; font-size: min(1.5em, 22cqh, calc(190cqw / (var(--n) * .6))); padding: .6em;
      border-radius: inherit; overflow-wrap: anywhere; }
    .wl-prayer.wp-bar .wp-notice { font-size: min(1em, 60cqh); white-space: nowrap; }
  `;
/** Hell oder dunkel als Schrift auf der Hervorhebungsfarbe. */
export function inkOn(color) {
    const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
    if (!m)
        return '#111111';
    const hex = m[1].length === 3 ? m[1].split('').map((c) => c + c).join('') : m[1];
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255).map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
    return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.4 ? '#111111' : '#ffffff';
}
/** Zeilen, die angezeigt werden (Sonnenaufgang optional). */
const shownKeys = (el) => PRAYER_KEYS.filter((k) => k !== 'sunrise' || el.showSunrise !== false);
export function renderPrayer(box, el, ctx) {
    const { doc } = ctx;
    const lang = isPrayerLang(el.lang) ? el.lang : 'de';
    const tx = PRAYER_TEXTS[lang];
    const timeZone = safeTimeZone(el.timeZone) ?? ctx.timeZone;
    const ref = tableRef(el);
    const hasCoords = el.lat != null && el.lon != null;
    if (!ref && !hasCoords)
        return placeholder(doc, box, 'Gebetszeiten', 'Ort (Koordinaten) oder Zeitplan wählen');
    const accent = safeColor(el.accent, '#E0B04B');
    const view = el.view ?? 'table';
    const cache = new Map();
    const state = () => prayerState(el, ctx.now(), { timeZone, tables: ctx.prayerTables, hijriAdjust: el.hijriAdjust ?? 0, cache });
    clear(box);
    const font = el.font ? ctx.fontStack(el.font) : undefined;
    const wrap = h(doc, 'div', {
        class: `wl-prayer ${view === 'bar' ? 'wp-bar' : view === 'column' ? 'wp-col' : 'wp-tbl'}`,
        style: `color:${cssSafe(el.color || '#fff')};font-size:${Number(el.size) || 3.2}cqh;--acc:${cssSafe(accent)};--ink:${inkOn(accent)};` +
            `text-align:${cssSafe(el.align || 'start')};` + (font ? `font-family:${font};` : ''),
        attrs: { dir: lang === 'ar' ? 'rtl' : 'ltr', lang },
    });
    const inner = h(doc, 'div', { class: 'wp-in' });
    wrap.appendChild(inner);
    box.appendChild(wrap);
    const fitter = prayerFitter(wrap, inner);
    const cdText = h(doc, 'span');
    const cdTime = h(doc, 'b');
    let sig = '';
    const name = (k, friday) => (k === 'dhuhr' && friday ? tx.names.jumuah : tx.names[k]);
    const draw = (st) => {
        clear(inner);
        const keys = shownKeys(el);
        const time = (i) => h(doc, 'span', { class: 'wp-tm', text: hhmmAt(st.day.at[i], timeZone) });
        /** Text mit Zeichenzahl (--n) für die Schriftgrösse aus der Box. */
        const txt = (cls, text, n = len(text)) => h(doc, 'span', { ...(cls ? { class: cls } : {}), text, style: `--n:${n}` });
        const iqText = (k, i) => {
            if (k === 'dhuhr' && st.jumuah.length)
                return st.jumuah.join('\u00a0· '); // Umbruch nur nach dem Punkt
            const iq = st.day.iqama[i];
            return iq != null && el.showIqama !== false ? `${tx.iqama} ${hhmmAt(iq, timeZone)}` : '';
        };
        const cd = el.showCountdown === false ? null : h(doc, 'div', { class: 'wp-cd', style: `color:${cssSafe(accent)}` }, [cdText, doc.createTextNode(' '), cdTime]);
        if (view === 'bar') {
            // Geschätzte Breite der Leiste in em (Name + Zeit + Abstände je Eintrag, Countdown); Feinabgleich danach gemessen
            let em = 1.2;
            for (const k of keys) {
                const i = PRAYER_KEYS.indexOf(k);
                em += len(name(k, st.friday)) * 0.58 + 3.9 + 0.7;
                inner.appendChild(h(doc, 'div', { class: `wp-it${st.current === k ? ' wp-cur' : ''}` }, [txt('wp-n', name(k, st.friday)), time(i)]));
            }
            if (cd) {
                em += len(cdLabel(st)) * 0.58 + 4.2;
                inner.appendChild(cd);
            }
            wrap.style.setProperty('--wp-len', em.toFixed(1));
        }
        else {
            const head = h(doc, 'div', { class: 'wp-ph' });
            const left = h(doc, 'div', { class: 'wp-dates' });
            if (el.title)
                head.appendChild(txt('wp-ttl', el.title));
            if (el.showDate !== false)
                left.appendChild(txt(undefined, gregorianText(st.date, lang)));
            if (el.showHijri !== false) {
                const hj = hijriText(st.date, lang, el.hijriAdjust ?? 0);
                if (hj)
                    left.appendChild(txt(undefined, hj));
            }
            if (left.childNodes.length)
                head.appendChild(left);
            if (cd)
                head.appendChild(cd);
            if (head.childNodes.length)
                inner.appendChild(head);
            if (view === 'column') {
                const rows = h(doc, 'div', { class: 'wp-rows' });
                for (const k of keys) {
                    const i = PRAYER_KEYS.indexOf(k);
                    rows.appendChild(h(doc, 'div', { class: `wp-row${st.current === k ? ' wp-cur' : ''}`, attrs: { 'data-prayer': k } }, [txt('wp-n', name(k, st.friday)), time(i), txt('wp-iq', iqText(k, i))]));
                }
                inner.appendChild(rows);
            }
            else {
                const grid = h(doc, 'div', { class: 'wp-grid' });
                for (const k of keys) {
                    const i = PRAYER_KEYS.indexOf(k);
                    grid.appendChild(h(doc, 'div', { class: `wp-cell${st.current === k ? ' wp-cur' : ''}`, attrs: { 'data-prayer': k } }, [txt('wp-n', name(k, st.friday), lineLen(name(k, st.friday))), time(i), txt('wp-iq', iqText(k, i), lineLen(iqText(k, i)))]));
                }
                inner.appendChild(grid);
            }
        }
        if (st.notice && el.notice) {
            const t = el.noticeText?.trim() || tx.notice;
            inner.appendChild(h(doc, 'div', { class: 'wp-notice', attrs: { role: 'status' }, text: t, style: `--n:${len(t)}` }));
        }
        if (cd)
            cd.style.setProperty('--n', String(len(cdLabel(st)) + 8));
        fitter.fit();
    };
    const tick = () => {
        const st = state();
        if (!st) {
            if (sig !== 'none') {
                sig = 'none';
                clear(inner);
                inner.appendChild(h(doc, 'div', { class: 'wp-cd', text: tx.noTimes, style: `--n:${len(tx.noTimes)}` }));
            }
            return;
        }
        const s = `${st.date}|${st.current}|${st.notice}|${st.next?.key}|${st.next?.kind}|${st.day.at.join(',')}`;
        if (s !== sig) {
            sig = s;
            draw(st);
        }
        if (st.next) {
            cdText.textContent = cdLabel(st);
            cdTime.textContent = formatCountdown(st.next.at - ctx.now());
        }
    };
    /** Beschriftung des Countdowns („Asr in", „Iqama Dhuhr in" …). */
    const cdLabel = (st) => {
        const n = st.next;
        if (!n)
            return '';
        const pn = name(n.key, n.kind === 'jumuah' || (n.key === 'dhuhr' && st.friday && st.jumuah.length > 0));
        return n.kind === 'jumuah' ? tx.nextJumuah : n.kind === 'iqama' ? tx.nextIqama(pn) : tx.next(pn);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => {
        clearInterval(t);
        fitter.stop();
    };
}
/** Zeichenzahl (Graphem-Näherung über Codepoints), mindestens 1. */
const len = (s) => Math.max(1, [...s].length);
/** Längste Zeile, wenn ein Text aus mehreren Wörtern auf zwei Zeilen umbricht (Namen und Iqama in der Tabelle). */
const lineLen = (s) => {
    const words = s.split(/\s+/);
    if (words.length < 2)
        return len(s);
    return Math.max(...words.map(len), Math.ceil(len(s) / 2) + 1);
};
/** Verkleinerungsstufen von --wp-fit, falls die Schätzung über Container-Einheiten nicht reicht (andere Schrift, lange Texte). */
const FIT_STEPS = [1, 0.94, 0.88, 0.82, 0.76, 0.7, 0.64, 0.58, 0.52, 0.46, 0.4];
/** true, wenn ein Element (oder ein Text darin) über seine Box hinausragt. */
function overflows(e) {
    const x = e;
    return x.scrollWidth > x.clientWidth + 1 || x.scrollHeight > x.clientHeight + 1;
}
/**
 * Nachmessen im Browser: verkleinert die Schrift gemeinsam, bis nichts mehr überläuft und kein Name abgeschnitten
 * ist. Ohne Layout (z. B. Tests ohne Browser) misst alles 0 und es bleibt bei der Grösse aus den Container-Einheiten.
 */
function prayerFitter(wrap, inner) {
    const win = wrap.ownerDocument.defaultView;
    const check = () => {
        if (overflows(inner))
            return true;
        for (const e of inner.querySelectorAll('.wp-cell, .wp-rows, .wp-row, .wp-it, .wp-n, .wp-ttl, .wp-cd, .wp-cd span, .wp-ph, .wp-notice')) {
            if (overflows(e))
                return true;
        }
        return false;
    };
    const fit = () => {
        if (!wrap.isConnected || !inner.clientWidth)
            return;
        for (const f of FIT_STEPS) {
            inner.style.setProperty('--wp-fit', String(f));
            if (!check())
                return;
        }
    };
    let raf = 0;
    let lastW = -1;
    let lastH = -1;
    const ro = win && 'ResizeObserver' in win
        ? new win.ResizeObserver(() => {
            if (wrap.clientWidth === lastW && wrap.clientHeight === lastH)
                return;
            lastW = wrap.clientWidth;
            lastH = wrap.clientHeight;
            if (raf)
                win.cancelAnimationFrame(raf);
            raf = win.requestAnimationFrame(() => {
                raf = 0;
                fit();
            });
        })
        : null;
    ro?.observe(wrap);
    // Webfonts können nach dem ersten Zeichnen nachladen und die Breiten ändern
    void wrap.ownerDocument.fonts?.ready.then(() => fit()).catch(() => undefined);
    return {
        fit,
        stop: () => {
            ro?.disconnect();
            if (raf && win)
                win.cancelAnimationFrame(raf);
        },
    };
}
//# sourceMappingURL=prayer-render.js.map